import { Component, Input, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { IFormOptions, IProperty } from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { INotesTemplateDTO, INotesTemplateEntityDTO, INotesTypeEntityDTO } from '@modeso/types__aumico-lib-notes-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { Observable, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { Errors } from '../../../enums/errors.enum';
import { NotesTemplatesFieldsName } from '../../../enums/notesTemplatesFieldsName.enum';
import { NotesTemplatesHelper } from '../../../helpers/notesTemplates.helper';
import { ILegalFormAndAccountBasis } from '../../../interfaces/ILegalFormAndAccountBasis.interface';
import { ILegalFormAndAccountBasisCombination } from '../../../interfaces/ILegalFormAndAccountBasisCombination.interface';
import { ILegalFormAndAccountBasisCombinationCheck } from '../../../interfaces/ILegalFormAndAccountBasisCombinationCheck.interface';
import { INameExistenceFiltration } from '../../../interfaces/INameExistenceFiltration.interface';
import { INotesTemplateFormValue } from '../../../interfaces/INotesTemplateFormValue.interface';
import { NotesTemplateMapper } from '../../../mappers/notesTemplates.mapper';
import { NotesProvider } from '../../../provider/notes.provider';
import { NotesTypesHelper } from './../../../helpers/notesTypes.helper';
import { INotesTemplatesFormDelegation } from './INotesTemplatesFormDelegation.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:NotesTemplatesFormPage');

@UntilDestroy()
@Component({
    selector: 'aumico-notes-templates-form-page',
    templateUrl: 'notes-templates-form.page.html',
    styleUrls: ['notes-templates-form.page.scss'],
})
export class NotesTemplatesFormPage implements OnInit {
    @Input() public set injectedProviderSetter(injectedProvider: NotesProvider) {
        if (injectedProvider) {
            this.provider = injectedProvider;
        }
    }

    @Input() languages: string[] = ['de-ch', 'en-us'];
    @Input() currentLanguage = 'de-ch';
    @Input() legalForms: ILegalFormAndAccountBasis[] = [];
    @Input() accountBases: ILegalFormAndAccountBasis[] = [];

    DELEGATE?: INotesTemplatesFormDelegation;
    @Input() public set delegate(delegate: INotesTemplatesFormDelegation) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }
    protected subscriptions: Subscription[] = [];
    public errorMessage: Observable<string>;

    data: INotesTemplateFormValue;
    formOptions: IFormOptions;
    tempFormOptions: IFormOptions;
    notesTypesSelections: INameId[] = [];
    selectedNotesTypes: INotesTypeEntityDTO[] = [];
    retrievedNotesTypes: INotesTypeEntityDTO[] = [];
    isNameExist = false;
    isLegalFormAndAccountBasiscombinationExist = false;
    fieldsName = NotesTemplatesFieldsName;

    readonly nameExistenceError = Errors.NameExistenceError;
    readonly legalFromAndAccountBasisCombinationError = Errors.legal_From_And_Account_Basis_Combination_Error;

    isBackendReturnError = false;
    backendErrors: string;

    constructor(
        private provider: NotesProvider,
        private notesTypeHelper: NotesTypesHelper,
        private notesTemplatesHelper: NotesTemplatesHelper,
        private notesTemplateMapper: NotesTemplateMapper,
    ) {}

    public ngOnInit(): void {
        this.prepareFormOptions();
        this.provider.dispatchNotesTypes();
        this.provider
            .getNotesTypes$()
            .pipe(untilDestroyed(this))
            .subscribe(this.getNotesTypesAndPrepareOptions.bind(this));
        this.prepareLegalFormAndAccountBasisOptions();
        this.provider
            .getActiveNotesTemplate$()
            .pipe(untilDestroyed(this))
            .subscribe(this.handleGetNotesTemplate.bind(this));
    }

    handleGetNotesTemplate(notesTemplate: INotesTemplateDTO) {
        if (notesTemplate) {
            debug('ngOnInit getActiveNotesTemplate result');
            debug(notesTemplate);
            this.data = this.notesTemplateMapper.prepareNotesTemplateOfUpdateMode(notesTemplate);
            debug(this.data, 'data');
            this.selectedNotesTypes = notesTemplate.notesTypes;
        }
    }

    prepareFormOptions() {
        this.tempFormOptions = {
            properties: [
                {
                    name: 'Name',
                    type: 'text',
                    property: this.fieldsName.NAME,
                    required: true,
                    extraValidators: [Validators.minLength(5)],
                },
                {
                    name: 'LegalForm',
                    type: 'select',
                    property: this.fieldsName.LEGAL_FORM,
                    required: true,
                    multiple: false,
                },
                {
                    name: 'Account Basis',
                    type: 'select',
                    property: this.fieldsName.ACCOUNT_BASIS,
                    required: true,
                    multiple: false,
                },
                {
                    name: 'Notes Types',
                    type: 'draggable-multi-select',
                    property: this.fieldsName.NOTES_TYPES,
                    required: true,
                },
            ],
            submitText: 'Save',
        };
        this.checkFormOptionsAreFullyReady();
    }

    onSubmit(result: INotesTemplateFormValue): void {
        debug('result of the form');
        debug(result);
        const notesTemplateRequest: INotesTemplateEntityDTO = this.notesTemplateMapper.prepareNotesTemplateRequest(
            result,
            this.data,
        );
        debug(result);
        // TODO: If owner couldn't be known in frontend this check should be removed otherwise will apply the check with the known owner
        // this.checkPreconditions(notesTemplateRequest);
        this.sendRequest(notesTemplateRequest);
    }

    checkPreconditions(request: INotesTemplateEntityDTO) {
        const combination: ILegalFormAndAccountBasisCombinationCheck = {
            accountBasis: request.accountBasis.id,
            legalForm: request.legalForm.id,
            id: this.data?._id,
            owner: request.owner,
        };

        const nameFiltration: INameExistenceFiltration = {
            name: request.name,
            owner: request.owner,
            id: this.data?._id,
        };

        this.provider
            .checkNotesTemplateNameExistence$(nameFiltration)
            .pipe(first(), untilDestroyed(this))
            .subscribe(this.handleNameExistenceCheck.bind(this));

        this.subscriptions.push(
            this.provider
                .checkLegalFormAndAccountBasisCombination$(combination)
                .pipe(first(), untilDestroyed(this))
                .subscribe(this.handleLegalFormAndAccountBasiscombination.bind(this)),
        );
    }

    sendRequest(request: INotesTemplateEntityDTO) {
        if (!this.data && !this.isNameExist && !this.isLegalFormAndAccountBasiscombinationExist) {
            this.provider.createNotesTemplate(request);
            this.DELEGATE.afterNotesTemplateSaved();
        }

        if (!this.isNameExist && !this.isLegalFormAndAccountBasiscombinationExist && this.data) {
            this.provider.editNotesTemplate(request);
            this.DELEGATE.afterNotesTemplateSaved();
        }
    }

    handleNameExistenceCheck(isNameExist: boolean) {
        debug('name existence', isNameExist);
        this.isNameExist = isNameExist;
    }

    handleLegalFormAndAccountBasiscombination(isCombinationExist: boolean) {
        debug('isCombinationExist existence', isCombinationExist);
        this.isLegalFormAndAccountBasiscombinationExist = isCombinationExist;
    }

    getNotesTypesAndPrepareOptions(retrievedNotesTypes: INotesTypeEntityDTO[]) {
        this.retrievedNotesTypes = retrievedNotesTypes;
        this.notesTypesSelections = this.notesTypeHelper.getIdsAndNamesOfNotesTypes(retrievedNotesTypes);

        this.tempFormOptions.properties.forEach((property: IProperty) => {
            if (property.property == this.fieldsName.NOTES_TYPES) {
                property.options = this.notesTypesSelections;
            }
        });

        this.checkFormOptionsAreFullyReady();
    }

    onFormChange(formValue: INotesTemplateFormValue) {
        debug(formValue);
        if (formValue.notesTypes) {
            this.selectedNotesTypes = this.notesTemplatesHelper.getSelectedNotesTypes(
                formValue.notesTypes,
                this.retrievedNotesTypes,
            );
        }
        debug(this.selectedNotesTypes);
    }

    prepareLegalFormAndAccountBasisOptions() {
        const currentLegalFormAndAccountBasis: ILegalFormAndAccountBasisCombination =
            this.notesTemplateMapper.getLegalFormAndAccountBasis(this.data);
        this.tempFormOptions.properties.forEach((property: IProperty) => {
            if (property.property == this.fieldsName.LEGAL_FORM) {
                property.options = this.notesTemplatesHelper.prepareLegalFormOrAccountBasisSelectionOptions(
                    this.legalForms,
                    currentLegalFormAndAccountBasis.legalForm,
                );
            }

            if (property.property == this.fieldsName.ACCOUNT_BASIS) {
                property.options = this.notesTemplatesHelper.prepareLegalFormOrAccountBasisSelectionOptions(
                    this.accountBases,
                    currentLegalFormAndAccountBasis.accountBasis,
                );
            }
        });

        this.checkFormOptionsAreFullyReady();
    }

    onCancel() {
        this.DELEGATE.cancelNotesTemplate();
    }

    private checkFormOptionsAreFullyReady() {
        const isLegalFormSelectionOptionsAdded = this.tempFormOptions.properties.find(
            (property) => property.property == this.fieldsName.LEGAL_FORM && property.options != null,
        );
        const isAccountBasisSelectionOptionsAdded = this.tempFormOptions.properties.find(
            (property) => property.property == this.fieldsName.ACCOUNT_BASIS && property.options != null,
        );
        const isNotesTypesSelectionOptionsAdded = this.tempFormOptions.properties.find(
            (property) => property.property == this.fieldsName.NOTES_TYPES && property.options != null,
        );

        const isFormOptionsAreReady =
            isLegalFormSelectionOptionsAdded &&
            isAccountBasisSelectionOptionsAdded &&
            isNotesTypesSelectionOptionsAdded;

        if (isFormOptionsAreReady) {
            this.formOptions = { ...this.tempFormOptions };
        }
    }
}
